import modelBaseV2 from "../model-base-v2.model";

/**
 * Incentive Calculation Model
 *
 * API wrappers for the FFIS calculation routes (the 8 OPERATOR routes of api/incentive/calc).
 * Every one is mapped to menu WM021103 "Calculation" and returns 403 until that menu is granted
 * to the caller's role, per country, in UserRoleMenuMap.
 *
 * Country and user are NOT sent from here. The backend reads them from the session, and each
 * stored procedure scopes its reads and writes by country - so a calcRunId belonging to another
 * country comes back as "not found" rather than as someone else's payout.
 *
 * ============================================================================================
 * WM021103 AND WM021104 MUST NOT BE GRANTED TO THE SAME ROLE
 * ============================================================================================
 * This page runs the payout calculation. WM021104 approves it. One role holding both means the
 * same person calculates and approves a payout, and no code here can prevent that - it is a
 * grant decision. Flagged because it is easy to grant both to "INCENTIVE_ADMIN" and never
 * notice.
 *
 * @author FFIS
 */
const IncentiveCalculationModel = {

  /* ---- calculation runs -------------------------------------------------- */

  /**
   * List runs, newest first.
   *
   * Note what a run's status does NOT tell you: whether it is the AUTHORITATIVE one. FFIS has
   * no flag for that until an approval is signed - IncentiveCalcRun.SupersededByRunId exists and
   * only IncentiveApprovalSignoff writes it - so every reader falls back to "the highest
   * completed non-simulation run". Show runType and statusCode together; they are what an
   * operator needs to tell a nightly batch figure from one they asked for.
   */
  searchRuns: (filters) => {
    const queryString = modelBaseV2.toQueryString(filters);
    return modelBaseV2.get(`/api/incentive/calc/run?${queryString}`);
  },

  /**
   * One run in full. Returns THREE result sets: the run header, a per-position summary, and the
   * exception list (rows that failed or produced a zero).
   *
   * The exception list is the point of this call. A run can report SUCCESS with hundreds of
   * people at zero, and the reasons are what distinguish "nobody sold anything" from "the
   * segment mapping is broken" - NO_SELLOUT_IN_PERIOD and SEGMENT_UNMAPPED both pay nothing and
   * mean completely different things.
   */
  getRunDetail: (calcRunId) => {
    return modelBaseV2.get(`/api/incentive/calc/run/${calcRunId}`);
  },

  /* ---- the FR-03 sanity-check gate -------------------------------------- */

  /**
   * Read the gate: target vs actual per segment, with each segment's confirmation state.
   *
   * varianceStatus is OK | WARN | FAIL, and FAIL DOES NOT MEAN "UNDERPERFORMED". The stored
   * procedure reserves FAIL for shapes that mean an INPUT IS MISSING - sellout with no target,
   * or a target with no sellout at all. Selling 60% of target is a business problem, comes back
   * WARN, and calculates normally. So a FAIL row is a reason to go and look at the upload, not
   * a number to accept.
   */
  getSanityCheck: (filters) => {
    const queryString = modelBaseV2.toQueryString(filters);
    return modelBaseV2.get(`/api/incentive/calc/sanitycheck?${queryString}`);
  },

  /**
   * Build (or rebuild) the gate's content from the current facts.
   *
   * REBUILDING DISCARDS ANY EXISTING CONFIRMATION, deliberately: a confirmation is one person's
   * statement about a particular set of numbers, so once the numbers change it means nothing.
   * The UI must warn before rebuilding a confirmed scope - the operator will have to review and
   * confirm it again, and if the period is mid-approval that is a real interruption.
   */
  buildSanityCheck: (body) => {
    return modelBaseV2.post('/api/incentive/calc/sanitycheck/build', { body });
  },

  /**
   * Confirm the gate. THIS IS WHAT UNLOCKS A PAYOUT CALCULATION for the scope.
   *
   * It is a named person accepting responsibility for the inputs, which is why FR-03 exists and
   * why the API takes a comment. Two things the UI must not do:
   *
   *   - do not confirm from the same click that built it. The operator has to be able to READ
   *     the variance rows in between, or the gate is a rubber stamp and FR-03 is not satisfied;
   *   - do not set acknowledgeFail silently. It is refused while any FAIL row stands unless the
   *     caller passes acknowledgeFail, and that flag is a deliberate statement that somebody
   *     looked at the failing segment and accepted it anyway. It goes into the record with the
   *     comment. Make the user tick it, and show them which segments they are overriding.
   */
  confirmSanityCheck: (body) => {
    return modelBaseV2.post('/api/incentive/calc/sanitycheck/confirm', { body });
  },

  /* ---- running a calculation -------------------------------------------- */

  /**
   * Run a real calculation for a scope. Refused while the FR-03 gate is unconfirmed.
   *
   * runType is the operator's declaration of intent and is NOT cosmetic:
   *   MANUAL_FULL   - a payout calculation. Gated, and approvable.
   *   BATCH_PERIOD  - a period-close calculation. Gated, and approvable.
   *
   * Do NOT offer BATCH_NIGHTLY here. That value belongs to the unattended nightly job, is EXEMPT
   * from the FR-03 gate so the batch can show promoters a provisional figure without a human
   * confirming inputs every night, and IncentiveApprovalSubmit REFUSES to approve a run marked
   * with it. Letting an operator pick it from a dropdown would produce a run that looks like a
   * payout, bypasses the gate, and then cannot be approved - with nothing on this screen
   * explaining why.
   */
  runCalculation: (body) => {
    return modelBaseV2.post('/api/incentive/calc/run', { body });
  },

  /**
   * Recalculate ONE employee, for a correction after their manpower or grading was fixed.
   *
   * This writes a new run covering that person only, so the period then has two completed runs.
   * Since nothing records which is authoritative, the reports will take the newer one for
   * EVERYBODY - including the people it did not recalculate, who will have no result rows in it.
   * Check what the reports show after using this; a single-employee correction is not a
   * substitute for re-running the scope.
   */
  runCalculationForUser: (userId, body) => {
    return modelBaseV2.post(`/api/incentive/calc/run/${userId}`, { body });
  },

  /**
   * Simulate. Writes a run marked IsSimulation, which no promoter ever reads
   * (IncentiveMyBreakdownGet filters it out) and which cannot be approved.
   *
   * EXEMPT FROM THE FR-03 GATE, on purpose: a simulation is how an operator investigates the
   * very discrepancy the gate is complaining about, so refusing it would leave them with no way
   * to look. Label simulation results clearly in the UI - the figures are real arithmetic over
   * unconfirmed inputs, which is exactly the kind of number that gets screenshotted and quoted.
   */
  simulate: (body) => {
    return modelBaseV2.post('/api/incentive/calc/simulate', { body });
  },
};

export default IncentiveCalculationModel;
